import { useEffect } from "react"

import { useSelector } from "react-redux"

import { selectGuestLinkedCalendar } from "@selectors/calendar"
import { Suggestion, SuggestionStatus } from "types/suggestions"
import dayjs from "utils/dayjs"

import "./index.scss"

interface Props {
  suggestion: Suggestion
  status: SuggestionStatus
  setStatus: (status: SuggestionStatus) => void
}

const statuses = {
  error: {
    className: "suggestion-item__header__status__error",
  },
  success: {
    className: "suggestion-item__header__status__success",
  },
  default: {
    className: "suggestion-item__header__status",
  },
}

const Status = ({ suggestion, status, setStatus }: Props) => {
  const linkedCalendar = useSelector(selectGuestLinkedCalendar)
  const isDecided = suggestion?.isDecided || suggestion?.decided
  const isWithdrawn = suggestion?.isWithdrawn
  const isPast =
    dayjs().isAfter(dayjs(suggestion.startDate)) && !isWithdrawn && !isDecided
  const isCalendarConflict = !!suggestion?.isCalendarConflict && !isDecided && !isWithdrawn

  // C1 fix (2026-07-03): this chip was gated on linkedCalendar (the GUEST-preview selector)
  // only, so a logged-in user opening the suggestion-detail popup never saw a conflict/available
  // chip regardless of their own synced calendar. useGameplans.js now computes
  // isCalendarConflict on every When suggestion for logged-in users too (see
  // src/components/NewTeeupGameplan/hooks/useGameplans.js), so use that as the "we have
  // conflict data for this suggestion" signal instead of requiring the guest link.
  const hasConflictData =
    !!linkedCalendar || suggestion?.isCalendarConflict !== undefined

  // ROOT CAUSE (2026-07-03, overlapping-When-conflict ticket, popup surface): `isPast` above is
  // really "has STARTED" (dayjs().isAfter(startDate)), not "has ENDED" -- so for any same-day
  // fixture, both overlapping suggestions flip to isPast=true the moment the EARLIER one's start
  // time passes, even while the LATER one is still upcoming/ongoing. This branch used to check
  // isPast before isCalendarConflict, so a same-day conflicting pair always rendered "Past"
  // instead of "Calendar conflict" -- the popup silently swallowed the exact case the ticket
  // fixture exercises. The expanded Game Plan list (SuggestionItem.js renderConflictLabel) never
  // gated on isPast at all, so this was also an inconsistency between the two surfaces, not just
  // a missing feature. Conflict now takes priority over the past marker; a withdrawn/decided
  // suggestion is unaffected (unchanged priority), and a past-but-non-conflicting suggestion
  // still shows "Past" exactly as before.
  const getStatusLabel = () => {
    if (isWithdrawn) {
      setStatus({
        label: "Withdrawn",
        className: statuses.default.className,
      })
    } else if (isDecided) {
      // QJ: no "Decided" badge/label anywhere in the build -- a decided
      // suggestion just shows its value, no status chip at all.
    } else if (isCalendarConflict && hasConflictData) {
      setStatus({
        label: "Calendar conflict",
        className: statuses.error.className,
      })
    } else if (isPast) {
      setStatus({
        label: "Past",
        className: statuses.default.className,
      })
    } else if (hasConflictData) {
      setStatus({
        label: "Available",
        className: statuses.success.className,
      })
    }
  }

  useEffect(() => {
    getStatusLabel()
  }, [isPast, isWithdrawn, isDecided, isCalendarConflict, hasConflictData])

  return (
    <>
      <div className={status.className}>{status.label}</div>
    </>
  )
}

export default Status
